import api from "../Interceptors/Interceptor";

export interface IUserDynamicTranslation {
    parameter: string;
    value: string;
}

export interface IDynamicTranslationModel {
    uuid?: string;
    languageCode: string;
    parameter: string;
    component?: string | null;
    value: string;
    fromGlobal?: boolean;
    customer?: { uuid?: string; name?: string } | null;
}

export type TranslationScope = "customer" | "global";

export interface IBulkSettingsChange {
    parameter: string;
    languageCode: string;
    value: string;
    uuid?: string;
    createOverride?: boolean;
    deleteOverride?: boolean;
}

export interface IBulkSettingsSaveResult {
    saved: number;
    skipped: number;
    failed: number;
    errors: string[];
    /** Effective merged rows after save (e.g. global fallback when an override was deleted). */
    resolved?: IDynamicTranslationModel[];
}

export interface ISettingsTranslationDeleteResult {
    deleted: number;
}

export class DynamicTranslationService {
    private static readonly modelUrl = "dynamic-translation";

    public async getTranslationsForUser(languageCode?: string): Promise<IUserDynamicTranslation[]> {
        const query =
            languageCode != null && languageCode !== ""
                ? `?languageCode=${encodeURIComponent(languageCode)}`
                : "";
        const response = await api.get(`v1/${DynamicTranslationService.modelUrl}/user${query}`, {});
        const raw = response.data?.data;
        return Array.isArray(raw) ? (raw as IUserDynamicTranslation[]) : [];
    }

    public async fetchTranslationsForScope(
        scope: TranslationScope,
        options?: { search?: string; component?: string; customer?: string; signal?: AbortSignal },
    ): Promise<IDynamicTranslationModel[]> {
        const params = new URLSearchParams({ scope });
        const trimmed = options?.search?.trim();
        if (trimmed) {
            params.set("search", trimmed);
        }
        const component = options?.component?.trim();
        if (component) {
            params.set("component", component);
        }
        const customer = options?.customer?.trim();
        if (customer) {
            params.set("customer", customer);
        }
        const response = await api.get(`v1/${DynamicTranslationService.modelUrl}/settings?${params.toString()}`, {
            signal: options?.signal,
        });
        const raw = response.data?.data;
        return Array.isArray(raw) ? (raw as IDynamicTranslationModel[]) : [];
    }

    public async saveBulkSettings(
        scope: TranslationScope,
        changes: IBulkSettingsChange[],
        customerUuid?: string,
    ): Promise<IBulkSettingsSaveResult> {
        const params = new URLSearchParams({ scope });
        const customer = customerUuid?.trim();
        if (customer) {
            params.set("customer", customer);
        }
        const response = await api.post(
            `v1/${DynamicTranslationService.modelUrl}/settings/bulk?${params.toString()}`,
            { changes },
        );
        const raw = response.data?.data as IBulkSettingsSaveResult | undefined;
        return {
            saved: raw?.saved ?? 0,
            skipped: raw?.skipped ?? 0,
            failed: raw?.failed ?? 0,
            errors: Array.isArray(raw?.errors) ? raw.errors : [],
            resolved: Array.isArray(raw?.resolved) ? (raw.resolved as IDynamicTranslationModel[]) : [],
        };
    }

    public async deleteSettingsTag(
        scope: TranslationScope,
        parameter: string,
        customerUuid?: string,
    ): Promise<ISettingsTranslationDeleteResult> {
        const params = new URLSearchParams({ scope, parameter });
        const customer = customerUuid?.trim();
        if (customer) {
            params.set("customer", customer);
        }
        const response = await api.delete(
            `v1/${DynamicTranslationService.modelUrl}/settings/tag?${params.toString()}`,
        );
        const raw = response.data?.data as ISettingsTranslationDeleteResult | undefined;
        return { deleted: raw?.deleted ?? 0 };
    }

    public async deleteSettingsLanguage(
        scope: TranslationScope,
        languageCode: string,
        customerUuid?: string,
    ): Promise<ISettingsTranslationDeleteResult> {
        const params = new URLSearchParams({ scope, languageCode });
        const customer = customerUuid?.trim();
        if (customer) {
            params.set("customer", customer);
        }
        const response = await api.delete(
            `v1/${DynamicTranslationService.modelUrl}/settings/language?${params.toString()}`,
        );
        const raw = response.data?.data as ISettingsTranslationDeleteResult | undefined;
        return { deleted: raw?.deleted ?? 0 };
    }
}
